import { AnimatePresence, motion } from "framer-motion";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "utils";
import log from "logbeacon";

interface Step {
  header: string;
  status: "finish" | "process";
  id?: string; // 可选的唯一ID，用于锚点定位
}

interface StepsComponentProps {
  steps: Step[];
  className?: string;
  onStepClick: (id: string) => void;
  activeHeader?: string;
}

const StepsComponent: React.FC<StepsComponentProps> = ({
  steps,
  className,
  onStepClick,
  activeHeader,
}) => {
  // 创建一个引用来访问容器元素
  const containerRef = useRef<HTMLDivElement>(null);
  // 创建一个引用来存储步骤元素
  const stepsRefs = useRef<(HTMLDivElement | null)[]>([]);
  // 记录上一次的步骤数量
  const prevStepsCountRef = useRef<number>(0);
  // 蒙层状态
  const [isOverlay, setIsOverlay] = useState(false);
  // 使用防抖定时器
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // const [selectedStep, setSelectedStep] = useState<string | null>(null);
  // 滚动到指定步骤的函数
  const scrollToStep = useCallback((index: number) => {
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const stepEl = stepsRefs.current[index];

    if (stepEl) {
      const stepRect = stepEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // 计算目标滚动位置
      const isStepAboveView = stepRect.top < containerRect.top;
      const isStepBelowView = stepRect.bottom > containerRect.bottom;

      if (isStepAboveView) {
        // 如果步骤在视图上方，滚动到步骤顶部
        container.scrollTop =
          container.scrollTop + (stepRect.top - containerRect.top) - 16;
      } else if (isStepBelowView) {
        // 如果步骤在视图下方，滚动到能看见完整步骤的位置
        container.scrollTop =
          container.scrollTop + (stepRect.bottom - containerRect.bottom) + 16;
      }
    }
  }, []);

  // 步骤变化时，确保新步骤在视图中可见
  useEffect(() => {
    if (containerRef.current) {
      // 检测是否有新步骤添加
      if (steps.length > prevStepsCountRef.current) {
        // 如果有新步骤添加，滚动到最新的步骤
        const newStepIndex = steps.length - 1;
        scrollToStep(newStepIndex);
      }

      // 更新上一次的步骤数量
      prevStepsCountRef.current = steps.length;
    }
  }, [steps, scrollToStep]);

  // 处理滚动事件，更新蒙层状态
  const handleScroll = useCallback(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 添加50ms的防抖
    debounceTimerRef.current = setTimeout(() => {
      if (containerRef.current) {
        const container = containerRef.current;
        // 当滚动位置大于5px时，开始显示蒙层
        const scrollThreshold = 5;

        // 添加滚动方向判断
        if (container.scrollTop > scrollThreshold) {
          setIsOverlay(true);
        } else {
          setIsOverlay(false);
        }
      }
    }, 50);
  }, []);

  // 添加滚动事件监听
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);

      // 初始检查
      handleScroll();

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <div className={cn("flex flex-col h-full items-start relative", className)}>
      {/* 顶部渐变蒙层 - 始终存在但透明度变化，避免布局变化 */}
      <div
        className="absolute top-0 left-0 w-full z-10 pointer-events-none"
        style={{
          opacity: isOverlay ? 1 : 0,
          transition: "opacity 0.3s ease-out",
        }}
      >
        <div className="w-full h-10 bg-gradient-to-b from-background_solid_tertiary 10% to-background_solid_tertiary/0 90%"></div>
      </div>
      <div
        className="absolute bottom-0 left-0 w-full z-10 pointer-events-none"
        style={{
          opacity: isOverlay ? 1 : 0,
          transition: "opacity 0.3s ease-out",
        }}
      >
        <div className="w-full h-10 bg-gradient-to-b from-background_solid_tertiary/0 10% to-background_solid_tertiary 90%"></div>
      </div>

      <div
        ref={containerRef}
        className="w-full flex flex-col gap-4 pt-6 pb-3 px-4 h-full overflow-y-auto no-scrollbar"
      >
        <AnimatePresence>
          {steps.map((step, index) => {
            // 判断当前步骤是否为completed状态
            const isCompletedStep = step.header === "Complete";
            const isActive = activeHeader === step.header;

            return (
              <motion.div
                data-openreplay-label="DeepSearch · StepsComponent · motion.div"
                key={step.header}
                id={step.header}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={cn("flex w-full cursor-pointer group")}
                onClick={() => log.debug(step.id)}
                ref={(el: HTMLDivElement | null) => {
                  stepsRefs.current[index] = el;
                }}
              >
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center pt-1">
                    {step.status === "finish" ? (
                      <div
                        className={cn(
                          "flex items-center group-hover:bg-content_primary bg-content_tertiary justify-center w-3.5 h-3.5 rounded-full",
                          {
                            "bg-content_brand_primary": isCompletedStep,
                            "bg-content_primary": isActive,
                          },
                        )}
                      >
                        <Icon
                          name="check"
                          className=""
                          size={12}
                          color={ospThemes.colors.content_inverse_primary}
                        />
                      </div>
                    ) : (
                      <Icon
                        name="ic_loading"
                        className="animate-spin"
                        size={14}
                        color={ospThemes.colors.content_tertiary}
                      />
                    )}
                  </div>

                  {!isCompletedStep &&
                    step.status !== "process" &&
                    index < steps.length - 1 && (
                      <div
                        className="w-px h-full"
                        style={{
                          background:
                            "linear-gradient(to bottom, #383838 55%, rgba(35, 35, 35, 0) 140%)",
                        }}
                      />
                    )}
                </div>

                <div
                  data-openreplay-label={`DeepSearch · StepsComponent · div | ${step.header}`}
                  onClick={() => {
                    // setSelectedStep(step.id || "");
                    onStepClick(step.id || "");
                  }}
                  className={cn(
                    "body_medium_m text-content_tertiary ml-2 pb-4 break-words group-hover:text-content_primary",
                    {
                      "text-content_primary": isActive,
                    },
                  )}
                >
                  {step.header}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StepsComponent;
